import type { PremiumPlan } from '../data/bhairav/premiumPlan';

// API Client for Bhairav Aaradhyaa Backend

// In production, always use same-origin /api path (Vercel rewrites to Railway backend).
// This bypasses ISPs (especially in India) that block the Railway subdomain directly.
// In development, use REACT_APP_API_URL or default to local backend.
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:4000/api');
const REQUEST_TIMEOUT_MS = 15_000; // 15 seconds

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  details?: any;
  status?: number;
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Try to restore token from localStorage
    this.authToken = localStorage.getItem('authToken');
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.authToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.authToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'An error occurred',
          message: data.message,
          details: data.details,
          status: response.status,
        };
      }

      return { ...data, status: response.status };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.error('API request timed out:', endpoint);
        return {
          success: false,
          error: 'Request timed out. Please try again.',
        };
      }
      console.error('API request failed:', error);
      return {
        success: false,
        error: 'Network error. Please check your connection.',
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // GET request
  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  // POST request
  async post<T>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  // PUT request
  async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Create singleton instance
export const api = new ApiClient(API_BASE_URL);

// Auth API
export const authApi = {
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    dateOfBirth?: string;
  }) => api.post<{ user: any; token: string }>('/auth/register', data),

  googleAuth: (idToken: string) =>
    api.post<{ user: any; isNewUser: boolean }>('/auth/google', { idToken }),

  getProfile: () => api.get<{ user: any; japaStats: any; achievements: any; lifePurposeProfile: any; hasLifePurposeAccess: boolean; canRetakeQuestionnaire: boolean }>('/auth/me'),

  verifyAge: (dateOfBirth: string) =>
    api.post<{ isAgeVerified: boolean; age: number }>('/auth/verify-age', { dateOfBirth }),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),
};

// Japa API
export const japaApi = {
  getStats: () => api.get<any>('/japa/stats'),

  syncStats: (stats: {
    totalCount: number;
    todayCount: number;
    currentStreak: number;
    longestStreak: number;
    totalSessions: number;
    totalTimeSeconds: number;
    averageCountPerSession: number;
    lastSessionDate?: string;
    lastResetDate?: string;
  }) => api.post<any>('/japa/sync', stats),

  recordSession: (session: {
    mantraName: string;
    count: number;
    durationSeconds: number;
    beadSpeed?: number;
    completedMalas?: number;
  }) => api.post<{ record: any; stats: any }>('/japa/session', session),

  getHistory: (limit?: number, offset?: number) =>
    api.get<{ records: any[]; total: number }>(`/japa/history?limit=${limit || 50}&offset=${offset || 0}`),

  getSettings: () => api.get<any>('/japa/settings'),

  updateSettings: (settings: {
    targetCount?: number;
    targetMalas?: number;
    selectedMantra?: string;
    soundEnabled?: boolean;
    vibrationEnabled?: boolean;
    beadSpeed?: string;
    dailyReminder?: boolean;
    reminderTime?: string;
  }) => api.put<any>('/japa/settings', settings),

  getAchievements: () => api.get<any[]>('/japa/achievements'),

  unlockAchievement: (achievement: {
    achievementId: string;
    title: string;
    description: string;
    category?: string;
    level?: number;
  }) => api.post<any>('/japa/achievements', achievement),

  resetDaily: () => api.post<any>('/japa/reset-daily'),
};

// Life Purpose API
export const lifePurposeApi = {
  checkAccess: () =>
    api.get<{ hasPaid: boolean; isAgeVerified: boolean; hasFullAccess: boolean }>('/life-purpose/access'),

  getProfile: () => api.get<any>('/life-purpose/profile'),

  saveQuestionnaire: (data: {
    [key: string]: any;
  }) => api.post<{ id: string; isRetake: boolean; profileActivated: boolean; reviewPending?: boolean; fullResultJson: any; message: string }>('/life-purpose/questionnaire', data),

  getResults: () => api.get<{
    reviewPending?: boolean;
    reviewStatus?: 'pending' | 'in_progress' | 'delivered';
    submittedAt?: string;
    profile?: any;
    bhairavFormDetails?: any;
    secondaryFormsDetails?: any[];
    recommendedPractices?: any[];
    reviewTier?: 'regular' | 'premium';
    reviewText?: string | null;
    personalResources?: { title: string; note: string; path: string | null }[];
    premiumPlan?: PremiumPlan | null;
  }>('/life-purpose/results'),

  getResultsHistory: () => api.get<any[]>('/life-purpose/results/history'),

  selectTier: (tierPath: string) =>
    api.post<{ selectedTierPath: string }>('/life-purpose/select-tier', { tierPath }),

  canRetake: () => api.get<{ canRetake: boolean }>('/life-purpose/can-retake'),

  switchActive: (profileId: string) =>
    api.post<any>('/life-purpose/switch-active', { profileId }),

  updateSituationSummary: (situationSummary: string) =>
    api.post<{ success: boolean }>('/life-purpose/situation-summary', { situationSummary }),
};

// Payments API
export const paymentsApi = {
  getPricing: () =>
    api.get<{
      amount: number;
      currency: string;
      displayAmount: string;
      provider: 'stripe' | 'instamojo';
      country: string;
      countryCode: string;
      tiers: {
        regular: { amount: number; currency: string; displayAmount: string };
        premium: { amount: number; currency: string; displayAmount: string };
      };
    }>('/payments/pricing'),

  createPaymentIntent: (tier: 'regular' | 'premium' = 'regular') =>
    api.post<{
      provider: 'stripe' | 'instamojo';
      sessionUrl?: string;
      paymentRequestId?: string;
      longUrl?: string;
      amount: number;
      currency: string;
      displayAmount: string;
    }>('/payments/create-intent', { tier }),

  verifyPayment: (paymentId: string) =>
    api.get<{ status: string; hasPaid: boolean }>(`/payments/verify/${paymentId}`),

  verifyStripeSession: (sessionId: string) =>
    api.post<{ verified: boolean; alreadyCompleted?: boolean; message?: string }>('/payments/verify-stripe-session', { sessionId }),

  verifyInstamojo: (paymentRequestId: string) =>
    api.post<{ verified: boolean; alreadyCompleted?: boolean; message?: string }>('/payments/verify-instamojo', { payment_request_id: paymentRequestId }),

  recoverPendingPayment: () =>
    api.post<{ recovered: boolean }>('/payments/recover-pending'),

  checkAccess: () => api.get<{ hasPaid: boolean }>('/payments/access'),

  getHistory: () =>
    api.get<{
      id: string;
      amount: number;
      currency: string;
      amountDisplay: string;
      status: string;
      createdAt: string;
    }[]>('/payments/history'),
};

// Users API (profile management, GDPR)
export const usersApi = {
  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    dateOfBirth?: string;
  }) => api.patch<{ user: any }>('/users/me', data),

  deleteAccount: () => api.delete<{ message: string }>('/users/me'),

  exportData: () => api.get<any>('/users/me/export'),
};

// Contact API
export const contactApi = {
  submit: (data: {
    name: string;
    email: string;
    phone?: string;
    subject: string;
    message: string;
    category?: string;
  }) => api.post<{ id: string; message: string }>('/contact', data),

  getCategories: () =>
    api.get<{ value: string; label: string }[]>('/contact/categories'),

  sendSupport: (data: {
    category: 'practice' | 'reading' | 'technical' | 'feedback';
    message: string;
  }) => api.post<{ id: string; message: string }>('/contact/support', data),
};

// Newsletter API
export const newsletterApi = {
  subscribe: (data: {
    email: string;
    firstName?: string;
    preferences?: string[];
  }) => api.post<{ message: string }>('/newsletter/subscribe', data),

  updatePreferences: (preferences: string[]) =>
    api.put<{ preferences: string[] }>('/newsletter/preferences', { preferences }),

  getPreferenceOptions: () =>
    api.get<{ value: string; label: string; description: string }[]>('/newsletter/preference-options'),
};

// Sadhana Tracking API
export const sadhanaApi = {
  getDashboard: () =>
    api.get<{
      tracking: {
        id: string;
        tierPath: string;
        totalDays: number;
        startDate: string;
        currentDay: number;
        isCompleted: boolean;
        completedAt: string | null;
      };
      todayRecord: {
        dayNumber: number;
        completed: boolean;
        completedPractices: string[];
      };
      todayDayNumber: number;
      currentWeek: number;
      streak: number;
      percentComplete: number;
      completedDays: number;
      completedDayNumbers: number[];
      sacredDays: Record<string, string>;
      notesByDay: Record<string, string>;
    }>('/sadhana/dashboard'),

  startPath: (tierPath: string) =>
    api.post<{
      id: string;
      tierPath: string;
      totalDays: number;
      startDate: string;
      currentDay: number;
    }>('/sadhana/start', { tierPath }),

  completeDay: (completedPractices: string[], note?: string) =>
    api.post<{
      record: {
        dayNumber: number;
        completed: boolean;
        completedPractices: string[];
      };
      currentDay: number;
      isCompleted: boolean;
    }>('/sadhana/complete-day', { completedPractices, ...(note ? { note } : {}) }),

  getCalendar: (year: number, month: number) =>
    api.get<{
      dayNumber: number;
      date: string;
      completed: boolean;
      completedPractices: string[];
    }[]>(`/sadhana/calendar/${year}/${month}`),
};

export interface PremiumOrder {
  id: string;
  displayName: string | null;
  age: number;
  lifeStage: string;
  experienceLevel: string;
  timeAvailable: string;
  primaryChallenges: string[];
  emotionalState: string[];
  spiritualGoals: string[];
  materialGoals: string[];
  practiceStyle: string[];
  situationSummary: string | null;
  recommendedForm: string | null;
  confidenceScore: number | null;
  fullResultJson: { primaryForm?: { slug: string; name: string; confidence: number } } | null;
  reviewStatus: 'pending' | 'in_progress' | 'delivered';
  reviewText: string | null;
  paidAt: string | null;
  createdAt: string;
  user: { email: string; firstName: string; lastName: string };
}

const adminHeaders = (secret: string): Record<string, string> => ({ 'x-admin-secret': secret });

export const premiumAdminApi = {
  queue: (secret: string) =>
    api.get<PremiumOrder[]>('/life-purpose/admin/premium-queue?include=delivered', adminHeaders(secret)),

  start: (secret: string, profileId: string) =>
    api.post<{ success: boolean }>(`/life-purpose/admin/premium-review/${profileId}/start`, undefined, adminHeaders(secret)),

  deliver: (secret: string, profileId: string, body: { reviewText: string; plan: PremiumPlan }) =>
    api.post<{ message: string }>(`/life-purpose/admin/premium-review/${profileId}`, body, adminHeaders(secret)),
};

export default api;
